export const createViewerMutationsController = ({
  state,
  element,
  apiRequest,
  thingNameInput,
  thingKindInput,
  thingDescriptionInput,
  thingAttributesInput,
  placementThingNameInput,
  placementThingKindInput,
  placementThingDescriptionInput,
  placementThingAttributesInput,
  existingThingSelect,
  placementExistingThingSelect,
  nodeMappingIntegrationSelect,
  nodeMappingObjectTypeSelect,
  nodeMappingExternalObjectSelect,
  placementNodeMappingIntegrationSelect,
  placementNodeMappingObjectTypeSelect,
  placementNodeMappingExternalObjectSelect,
  mappingPayloadForSelection,
  placementPayloadForSelection,
  selectedMapping,
  selectedNodeKey,
  selectedObjectLabel,
  selectedPlacementPosition,
  selectedOptionMetadata,
  mergeMapping,
  mergePlacement,
  mergeNodeMapping,
  syncMappings,
  syncNodeMappings,
  syncPlacements,
  syncNavigationPoints,
  selectNavigationPoint,
  focusOnNavigationPoint,
  navigationPointLabel,
  setFeedback,
  is2DLikeMode
}) => {
  const parseAttributesInput = (input, message = "Attributes JSON is invalid") => {
    const raw = input?.value.toString().trim()
    if (!raw) return {}

    try {
      return JSON.parse(raw)
    } catch (_error) {
      throw new Error(message)
    }
  }

  const saveSelectedThing = async () => {
    if (!state.selectedObject) {
      setFeedback("Select a node first.", "error")
      return
    }

    const basePayload = mappingPayloadForSelection()
    let payload = null
    const mapping = selectedMapping()

    try {
      const thingPayload = {
        name: thingNameInput?.value.toString().trim(),
        kind: thingKindInput?.value.toString().trim(),
        description: thingDescriptionInput?.value.toString().trim(),
        attributes: parseAttributesInput(thingAttributesInput)
      }

      if (!mapping && !thingPayload.name) {
        setFeedback("Thing name is required.", "error")
        return
      }

      if (mapping) {
        payload = await apiRequest(`${element.dataset.mappingsUrl}/${mapping.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            ...basePayload,
            thing: thingPayload
          })
        })
      } else {
        payload = await apiRequest(element.dataset.mappingsUrl, {
          method: "POST",
          body: JSON.stringify({
            ...basePayload,
            thing: thingPayload
          })
        })
      }

      mergeMapping(payload.mapping)
      state.things = payload.things || state.things
      setFeedback("Tag saved successfully.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const savePlacedThing = async () => {
    const placementPosition = selectedPlacementPosition()
    if (!state.selectedPlacement && !placementPosition) {
      setFeedback("Select a placement point first.", "error")
      return
    }

    let payload = null

    try {
      const thingPayload = {
        name: placementThingNameInput?.value.toString().trim(),
        kind: placementThingKindInput?.value.toString().trim(),
        description: placementThingDescriptionInput?.value.toString().trim(),
        attributes: parseAttributesInput(placementThingAttributesInput)
      }

      if (!state.selectedPlacement && !thingPayload.name) {
        setFeedback("Thing name is required.", "error")
        return
      }

      if (state.selectedPlacement) {
        payload = await apiRequest(`${element.dataset.placementsUrl}/${state.selectedPlacement.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            ...placementPayloadForSelection(),
            thing: thingPayload
          })
        })
        setFeedback(`Updated ${payload.placement.thing.name}.`, "success")
      } else {
        payload = await apiRequest(element.dataset.placementsUrl, {
          method: "POST",
          body: JSON.stringify({
            ...placementPayloadForSelection(),
            thing: thingPayload
          })
        })
        setFeedback(`Created and placed ${payload.placement.thing.name}.`, "success")
      }

      mergePlacement(payload.placement)
      state.things = payload.things || state.things
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const attachExistingThing = async () => {
    if (!state.selectedObject) {
      setFeedback("Select a node first.", "error")
      return
    }

    if (!existingThingSelect?.value) {
      setFeedback("Choose a thing to attach.", "error")
      return
    }

    try {
      const payload = await apiRequest(element.dataset.mappingsUrl, {
        method: "POST",
        body: JSON.stringify({
          ...mappingPayloadForSelection(),
          thing_id: existingThingSelect.value
        })
      })

      mergeMapping(payload.mapping)
      state.things = payload.things || state.things
      setFeedback("Tag saved successfully.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const saveSelectedNodeMapping = async () => {
    if (!state.selectedObject) {
      setFeedback("Select a node first.", "error")
      return
    }

    if (!nodeMappingIntegrationSelect?.value || !nodeMappingObjectTypeSelect?.value || !nodeMappingExternalObjectSelect?.value) {
      setFeedback("Choose an integration, object type, and external object.", "error")
      return
    }

    const selectedOption = nodeMappingExternalObjectSelect.selectedOptions[0]

    try {
      const payload = await apiRequest(element.dataset.nodeMappingsUrl, {
        method: "POST",
        body: JSON.stringify({
          integration_id: nodeMappingIntegrationSelect.value,
          object_type: nodeMappingObjectTypeSelect.value,
          external_id: nodeMappingExternalObjectSelect.value,
          external_label: selectedOption?.textContent?.trim(),
          model_node_key: selectedNodeKey(),
          model_node_name: selectedObjectLabel(),
          metadata: selectedOptionMetadata(selectedOption)
        })
      })

      mergeNodeMapping(payload.node_mapping, payload.integrations || state.integrations)
      setFeedback(`Mapped ${selectedObjectLabel()} to ${selectedOption?.textContent?.trim() || nodeMappingExternalObjectSelect.value}.`, "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const saveSelectedPlacementNodeMapping = async () => {
    if (!state.selectedPlacement) {
      setFeedback("Select a placed object first.", "error")
      return
    }

    if (!placementNodeMappingIntegrationSelect?.value || !placementNodeMappingObjectTypeSelect?.value || !placementNodeMappingExternalObjectSelect?.value) {
      setFeedback("Choose an integration, object type, and external object.", "error")
      return
    }

    const selectedOption = placementNodeMappingExternalObjectSelect.selectedOptions[0]

    try {
      const payload = await apiRequest(element.dataset.nodeMappingsUrl, {
        method: "POST",
        body: JSON.stringify({
          integration_id: placementNodeMappingIntegrationSelect.value,
          object_type: placementNodeMappingObjectTypeSelect.value,
          external_id: placementNodeMappingExternalObjectSelect.value,
          external_label: selectedOption?.textContent?.trim(),
          thing_placement_id: state.selectedPlacement.id,
          model_node_name: state.selectedPlacement.thing.name,
          metadata: selectedOptionMetadata(selectedOption)
        })
      })

      mergeNodeMapping(payload.node_mapping, payload.integrations || state.integrations)
      setFeedback(`Mapped ${state.selectedPlacement.thing.name} to ${selectedOption?.textContent?.trim() || placementNodeMappingExternalObjectSelect.value}.`, "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const removeNodeMapping = async (nodeMappingId) => {
    try {
      const payload = await apiRequest(`${element.dataset.nodeMappingsUrl}/${nodeMappingId}`, {
        method: "DELETE",
        body: JSON.stringify({})
      })

      syncNodeMappings(payload.node_mappings || [], payload.integrations || state.integrations)
      setFeedback("Removed external mapping.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const attachExistingPlacedThing = async () => {
    const placementPosition = selectedPlacementPosition()
    if (!state.selectedPlacement && !placementPosition) {
      setFeedback("Select a placement point first.", "error")
      return
    }

    if (!placementExistingThingSelect?.value) {
      setFeedback("Choose a thing to place.", "error")
      return
    }

    try {
      const payload = state.selectedPlacement
        ? await apiRequest(`${element.dataset.placementsUrl}/${state.selectedPlacement.id}`, {
            method: "PATCH",
            body: JSON.stringify({
              ...placementPayloadForSelection(),
              thing_id: placementExistingThingSelect.value
            })
          })
        : await apiRequest(element.dataset.placementsUrl, {
            method: "POST",
            body: JSON.stringify({
              ...placementPayloadForSelection(),
              thing_id: placementExistingThingSelect.value
            })
          })

      mergePlacement(payload.placement)
      state.things = payload.things || state.things
      setFeedback(`Placed ${payload.placement.thing.name}.`, "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const createNavigationPoint = async (pose) => {
    if (!element.dataset.navigationPointsUrl || !pose?.position) return

    try {
      const viewModeAtCreation = state.viewMode
      const payload = await apiRequest(element.dataset.navigationPointsUrl, {
        method: "POST",
        body: JSON.stringify({
          navigation_point: {
            position: pose.position,
            rotation: pose.rotation || {},
            metadata: {}
          }
        })
      })

      syncNavigationPoints(payload.navigation_points || [])
      if (payload.navigation_point?.id && state.navigationPointsById.has(payload.navigation_point.id)) {
        selectNavigationPoint(state.navigationPointsById.get(payload.navigation_point.id))
      }
      if (!is2DLikeMode(viewModeAtCreation) && state.selectedNavigationPoint) {
        focusOnNavigationPoint(state.selectedNavigationPoint)
      }
      setFeedback(`Added ${navigationPointLabel(state.selectedNavigationPoint)}.`, "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const removeNavigationPoint = async (navigationPointId = state.selectedNavigationPoint?.id) => {
    if (!navigationPointId || !element.dataset.navigationPointsUrl) return

    try {
      const payload = await apiRequest(`${element.dataset.navigationPointsUrl}/${navigationPointId}`, {
        method: "DELETE",
        body: JSON.stringify({})
      })

      syncNavigationPoints(payload.navigation_points || [])
      setFeedback("Removed navigation point.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const removeSelectedMapping = async () => {
    const mapping = selectedMapping()
    if (!mapping) return

    try {
      const payload = await apiRequest(`${element.dataset.mappingsUrl}/${mapping.id}`, {
        method: "DELETE",
        body: JSON.stringify({})
      })

      state.things = payload.things || state.things
      syncMappings(payload.mappings || [])
      setFeedback("Tag removed successfully.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const removePlacement = async (placementId = state.selectedPlacement?.id) => {
    if (!placementId) return

    try {
      const payload = await apiRequest(`${element.dataset.placementsUrl}/${placementId}`, {
        method: "DELETE",
        body: JSON.stringify({})
      })

      syncPlacements(payload.placements || [])
      setFeedback("Removed placed object.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  const removeSelectedPlacement = async () => {
    if (!state.selectedPlacement) return
    await removePlacement(state.selectedPlacement.id)
  }

  const confirmAndRemovePlacement = async (placementId) => {
    const placement = state.placementsById.get(placementId)
    if (!placement) return

    const confirmed = window.confirm(`Delete placed object "${placement.thing.name}"?`)
    if (!confirmed) return

    await removePlacement(placementId)
  }

  return {
    saveSelectedThing,
    savePlacedThing,
    attachExistingThing,
    saveSelectedNodeMapping,
    saveSelectedPlacementNodeMapping,
    removeNodeMapping,
    attachExistingPlacedThing,
    createNavigationPoint,
    removeNavigationPoint,
    removeSelectedMapping,
    removePlacement,
    removeSelectedPlacement,
    confirmAndRemovePlacement
  }
}
